import { jsx } from '@emotion/react';
import { FullStoryAPI } from '@zapier/react-fullstory';
import { formatTimeSpan, formatDateTime } from '@zapier/date';
import { Icon, Link, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { emitUserInteractionClick, InteractionGoal, InteractionName, } from '../../../../utils/avroEvents';
import { useUniversalNavContext } from '../../../../hooks';
import UsageBar from './UsageBar';
import { baseReset, boxSizing } from '../../../../../utils/style-mixins/reset';
const Styles = labelStyles('UniversalSidebarUsage', {
    root: Object.assign(Object.assign({}, boxSizing), { display: 'grid', gap: 'var(--zds-space-12, 12px)', whiteSpace: 'nowrap' }),
    header: Object.assign(Object.assign({}, boxSizing), { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--zds-space-12, 12px)', alignItems: 'center', font: Typography.base.fontFamily, fontSize: 16, fontWeight: 600, color: 'var(--zds-text-default, #413735)', '& svg': {
            color: 'inherit',
        } }),
    planName: Object.assign(Object.assign({}, baseReset), { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', font: `var(--zds-body-2)` }),
    usages: Object.assign(Object.assign({}, boxSizing), { display: 'grid', gap: 'var(--zds-space-8, 8px)', marginBottom: 'var(--zds-space-8, 8px)' }),
    resetInfo: Object.assign(Object.assign({}, baseReset), { display: 'grid', gap: 'var(--zds-space-8, 8px)', font: `var(--zds-minimal-print-1)`, gridTemplateRows: '1fr auto', paddingTop: '10px', color: 'var(--zds-text-weakest, #6F6765)', '> a': {
            color: 'var(--zds-text-link, #695BE8) !important',
            width: 'max-content',
            '&:hover': {
                color: 'var(--zds-text-link-hover, #503ebd) !important',
            },
            '&:focus-visible': {
                outlineColor: 'var(--zds-text-link, #695BE8) !important',
            },
            '&:focus': {
                outlineColor: 'none !important',
            },
        } }),
});
const Usages = (props) => {
    const hasUsedExtraTasks = props.isPlanMetered && props.numTasks > props.numFreeTasksMax;
    const usageBarLabel = props.isWorkspace
        ? 'Tasks used'
        : props.isPlanMetered
            ? 'Included Tasks'
            : 'Tasks';
    return (jsx("div", { css: Styles.usages, "data-zds": true },
        jsx(UsageBar, { label: usageBarLabel, max: props.isPlanMetered ? props.numFreeTasksMax : props.numTasksMax, value: props.isPlanMetered
                ? Math.min(props.numTasks, props.numFreeTasksMax)
                : props.numTasks }),
        hasUsedExtraTasks && (jsx(UsageBar, { label: "Additional Tasks", max: props.numTasksMax - props.numFreeTasksMax, value: props.numTasks - props.numFreeTasksMax })),
        !props.isWorkspace && (jsx(UsageBar, { label: "Zaps", max: props.numZapsMax, value: props.numZaps }))));
};
const PlanUsage = ({ numTasks, numTasksMax, numZaps, numZapsMax, resetDate, children = undefined, trialPlanName = undefined, trialPlanEndDate = undefined, isPlanMetered = false, numFreeTasksMax = 0, managePlanHref = '/app/settings/billing', managePlanLabel = 'Manage plan', title = 'Usage', isWorkspace = false, }) => {
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const managePlanURL = getFullyQualifiedURL(managePlanHref);
    const relativeDate = formatTimeSpan(Date.now(), resetDate, false);
    const handleEmitAvroClickEvent = (event) => {
        event.preventDefault();
        FullStoryAPI('event', `Plan usage click from UniversalLayout sidebar`);
        emitUserInteractionClick(InteractionName.plan_usage_click, InteractionGoal.plan_usage, managePlanLabel, managePlanHref).finally(() => (location.href = managePlanURL || ''));
    };
    return (jsx("section", { css: Styles.root, "data-zds": true },
        jsx("div", { css: Styles.header, "data-zds": true },
            jsx(Icon, { name: "miscWallet", size: 20 }),
            jsx("h3", { css: Styles.planName, "data-zds": true }, trialPlanName ? `${trialPlanName} plan (Trial)` : title)),
        jsx("div", { "data-zds": true },
            jsx(Usages, { isPlanMetered,
                numFreeTasksMax,
                numTasks,
                numTasksMax,
                numZaps,
                numZapsMax,
                isWorkspace }),
            jsx("p", { css: Styles.resetInfo, "data-zds": true },
                jsx("span", { "data-zds": true }, trialPlanName && trialPlanEndDate
                    ? `Trial ends on ${formatDateTime(trialPlanEndDate, 'default')}`
                    : `Usage resets in ${relativeDate}`),
                !isWorkspace && (jsx(Link, { onClick: handleEmitAvroClickEvent, href: managePlanURL }, managePlanLabel)))),
        children));
};
export default PlanUsage;
//# sourceMappingURL=PlanUsage.js.map
// @zapier/universal-layout@11.2.3 (b1a6e8603)
